/**
 * FAZ 5 / F5-10 — Sayfalanmis API yanitlari icin uyum katmani.
 *
 * ── ARKA PLAN ─────────────────────────────────────────────────────────────
 * Liste uclari `T[]` yerine `{ items, meta }` donmeye basladi (rapor §5.1).
 * Amac: 2000 satirlik listelerin tek istekte cekilmesini onlemek.
 *
 * ── 🔴 EN ONEMLI KURAL ────────────────────────────────────────────────────
 * `unwrapPage()` YALNIZCA ILK SAYFAYI dondurur. Sayfalama arayuzu OLMAYAN
 * bir listede bunu kullanmak, kullaniciya SESSIZCE 20 kayit gostermek
 * demektir — 2000 kayitlik bir listede kimse eksikligi fark etmez. Bu,
 * yavas yanittan DAHA KOTUDUR.
 *
 * Sayfalama arayuzu yoksa `fetchAllPages()` kullanin.
 */

export type PaginationMeta = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export type Paginated<T> = { items: T[]; meta: PaginationMeta };

/** Yanit sayfalanmis zarf mi? */
export function isPaginated<T>(value: unknown): value is Paginated<T> {
  return Boolean(
    value &&
      typeof value === 'object' &&
      Array.isArray((value as Paginated<T>).items) &&
      typeof (value as Paginated<T>).meta === 'object',
  );
}

/**
 * Zarfi acar.
 *
 * ⚠️ HEM ESKI (`T[]`) HEM YENI (`{items, meta}`) sekli kabul eder. Boylece
 * bir uc henuz donusturulmemisse de calisir; backend ve frontend'in ayni
 * anda deploy edilmesi zorunlu degil.
 *
 * 🔴 YALNIZCA ILK SAYFA. Bkz. dosya basindaki uyari.
 */
export function unwrapPage<T>(value: Paginated<T> | T[] | null | undefined): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (isPaginated<T>(value)) return value.items;
  return [];
}

export function pageMeta(value: unknown): PaginationMeta | null {
  return isPaginated(value) ? value.meta : null;
}

/**
 * TUM sayfalari cekip tek dizi dondurur.
 *
 * Sayfalama arayuzu olmayan listeler icin (acilir kutular, disari aktarma,
 * toplam sayim gerektiren ekranlar) DOGRU secim budur.
 *
 * ⚠️ `maxPages` bir GUVENLIK FRENI. Backend'de bir hata `totalPages`i cok
 * buyuk dondururse sonsuz donguye girilmesin. Sinira takilirsa konsola
 * uyari basilir — sessizce eksik veri donmez.
 *
 * @param fetchPage `(page, limit)` alip ham yaniti donduren fonksiyon.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number, limit: number) => Promise<Paginated<T> | T[]>,
  options: { limit?: number; maxPages?: number } = {},
): Promise<T[]> {
  const limit = options.limit ?? 100;
  const maxPages = options.maxPages ?? 50;

  const first = await fetchPage(1, limit);
  if (Array.isArray(first)) return first; // eski sekil — tek istekte hepsi

  const all = [...first.items];
  const totalPages = Math.max(1, Number(first.meta?.totalPages) || 1);
  const lastPage = Math.min(totalPages, maxPages);

  for (let page = 2; page <= lastPage; page += 1) {
    const next = await fetchPage(page, limit);
    all.push(...unwrapPage<T>(next));
  }

  if (totalPages > maxPages) {
    // eslint-disable-next-line no-console
    console.warn(
      `[pagination] ${totalPages} sayfadan yalnizca ${maxPages} cekildi (maxPages siniri). ` +
        'Bu liste icin sayfalama arayuzu eklenmeli.',
    );
  }
  return all;
}

/**
 * FAZ 5 / F5-10 — axios tabanli servisler icin "tum sayfalari cek" sarmalayicisi.
 *
 * ⚠️ `axiosInstance` DISARIDAN veriliyor: bu dosyanin onu import etmesi
 * dairesel bagimlilik yaratirdi.
 *
 * Cagiran sozlesmesi korunur — yine `{ data: T[] }` doner, yani mevcut
 * bilesenler degismeden calisir. Kazanc backend'de: her istek en fazla 100
 * kayit, DB'de sinirsiz tarama ve tek seferde dev bir serilestirme yok.
 */
export async function fetchAllPagesVia<T = unknown>(
  axiosInstance: { get: (url: string, config?: { params?: Record<string, unknown> }) => Promise<{ data: unknown }> },
  url: string,
  params: Record<string, unknown> = {},
  options: { limit?: number; maxPages?: number } = {},
): Promise<{ data: T[] }> {
  const items = await fetchAllPages<T>(
    async (page, limit) => {
      const res = await axiosInstance.get(url, { params: { ...params, page, limit } });
      return res.data as never;
    },
    options,
  );
  return { data: items };
}
